// client/src/pages/common/AccountPending.js
// ============================================
// ACCOUNT PENDING PAGE
// Shown to users when their account is awaiting approval or has been rejected
// ============================================

import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';

const AccountPending = () => {
  const { t } = useTranslation(['auth', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get status info from navigation state (passed from login)
  const { accountStatus, rejectionReason, userType, companyName } = location.state || {};
  
  // Default to pending if no status provided
  const status = accountStatus || 'pending';
  const type = userType || 'business';

  // Render different content based on account status
  const renderContent = () => {
    switch (status) {
      case 'pending':
        return <PendingContent companyName={companyName} userType={type} t={t} />;
      case 'rejected':
        return <RejectedContent reason={rejectionReason} companyName={companyName} userType={type} t={t} />;
      case 'suspended':
        return <SuspendedContent companyName={companyName} userType={type} t={t} />;
      default:
        return <PendingContent companyName={companyName} userType={type} t={t} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-lg w-full">
          {renderContent()}
          
          {/* Action Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={type === 'wholesaler' ? '/wholesaler/login' : '/login'}
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors font-medium"
            >
              ← Back to Login
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Go to Homepage
            </Link>
          </div>

          {/* Contact Support */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm">
              Have questions? Contact our support team at{' '}
              <a href="mailto:support@wholesalehub.gr" className="text-blue-600 hover:underline">
                support@wholesalehub.gr
              </a>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// ============================================
// STATUS-SPECIFIC COMPONENTS
// ============================================

const PendingContent = ({ companyName, userType, t }) => (
  <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
    {/* Header */}
    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 px-8 py-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4">
          <span className="text-5xl">⏳</span>
        </div>
        <h1 className="text-2xl font-bold text-white">
          {t('auth:accountPending.title', 'Account Pending Approval')}
        </h1>
      </div>
    </div>

    {/* Content */}
    <div className="px-8 py-6">
      {companyName && (
        <p className="text-center text-gray-600 mb-4">
          Welcome, <span className="font-semibold text-gray-900">{companyName}</span>!
        </p>
      )}
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <p className="text-yellow-800 text-center">
          {t('auth:accountPending.message', 
            'Your account registration is being reviewed by our team. This process typically takes 1-2 business days.'
          )}
        </p>
      </div>

      <h3 className="font-semibold text-gray-800 mb-3">What happens next?</h3>
      <ul className="space-y-3">
        <TimelineItem 
          icon="📋" 
          title="Application Review" 
          description="Our team is verifying your business information and VAT number."
          active
        />
        <TimelineItem 
          icon="✅" 
          title="Approval Notification" 
          description="You'll receive an email once your account is approved."
        />
        <TimelineItem 
          icon="🚀" 
          title="Start Using WholesaleHub" 
          description={userType === 'wholesaler' 
            ? "List your products and start receiving orders!"
            : "Browse wholesalers and place your first order!"
          }
        />
      </ul>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-800 mb-2">💡 Did you know?</h4>
        <p className="text-blue-700 text-sm">
          We verify all accounts to ensure a trusted marketplace for businesses in Crete. 
          This helps protect both buyers and sellers.
        </p>
      </div>
    </div>
  </div>
);

const RejectedContent = ({ reason, companyName, userType, t }) => (
  <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
    {/* Header */}
    <div className="bg-gradient-to-r from-red-500 to-red-600 px-8 py-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4">
          <span className="text-5xl">❌</span>
        </div>
        <h1 className="text-2xl font-bold text-white">
          {t('auth:accountRejected.title', 'Registration Not Approved')}
        </h1>
      </div>
    </div>

    {/* Content */}
    <div className="px-8 py-6">
      {companyName && (
        <p className="text-center text-gray-600 mb-4">
          Dear <span className="font-semibold text-gray-900">{companyName}</span>,
        </p>
      )}

      <p className="text-gray-700 text-center mb-6">
        {t('auth:accountRejected.message',
          'Unfortunately, we were unable to approve your account registration at this time.'
        )}
      </p>

      {reason && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-red-800 mb-2">Reason for Rejection:</h3>
          <p className="text-red-700">{reason}</p>
        </div>
      )}

      <div className="border-t border-gray-200 pt-6">
        <h3 className="font-semibold text-gray-800 mb-3">What can you do?</h3>
        <ul className="space-y-2 text-gray-600">
          <li className="flex items-start">
            <span className="mr-2">1.</span>
            <span>Review the rejection reason above</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">2.</span>
            <span>Correct any issues with your registration information</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">3.</span>
            <span>
              <Link 
                to={userType === 'wholesaler' ? '/wholesaler/register' : '/register'} 
                className="text-blue-600 hover:underline"
              >
                Submit a new registration
              </Link>
              {' '}with accurate information
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">4.</span>
            <span>Contact support if you believe this was a mistake</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
);

const SuspendedContent = ({ companyName, userType, t }) => (
  <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
    {/* Header */}
    <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-8 py-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4">
          <span className="text-5xl">🔒</span>
        </div>
        <h1 className="text-2xl font-bold text-white">
          {t('auth:accountSuspended.title', 'Account Suspended')}
        </h1>
      </div>
    </div>

    {/* Content */}
    <div className="px-8 py-6">
      {companyName && (
        <p className="text-center text-gray-600 mb-4">
          Account: <span className="font-semibold text-gray-900">{companyName}</span>
        </p>
      )}

      <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mb-6">
        <p className="text-gray-700 text-center">
          {t('auth:accountSuspended.message',
            'Your account has been temporarily suspended. This may be due to a policy violation or billing issue.'
          )}
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">📞 Contact Support</h4>
        <p className="text-blue-700 text-sm">
          Please contact our support team to resolve this issue and restore your account access.
        </p>
        <a 
          href="mailto:support@wholesalehub.gr" 
          className="inline-block mt-3 text-blue-600 hover:underline font-medium"
        >
          support@wholesalehub.gr
        </a>
      </div>
    </div>
  </div>
);

// ============================================
// HELPER COMPONENTS
// ============================================

const TimelineItem = ({ icon, title, description, active }) => (
  <li className={`flex items-start ${active ? 'opacity-100' : 'opacity-60'}`}>
    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
      active ? 'bg-yellow-100' : 'bg-gray-100'
    }`}>
      <span className="text-lg">{icon}</span>
    </div>
    <div>
      <p className={`font-medium ${active ? 'text-gray-900' : 'text-gray-600'}`}>{title}</p>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  </li>
);

export default AccountPending;